import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { cn } from '@/lib/utils.ts';
import { TemplateVariableDefinitionDto } from '@/features/dashboard/messageTemplates/types/TemplateVariableDefinitionDto.ts';
import { TemplateVariable } from './extensions/TemplateVariable.ts';
import { VariableSuggestion } from './extensions/VariableSuggestion.ts';
import { VariablesProvider } from './contexts/VariablesContext.tsx';
import { convertPlainTextToTiptap } from './converters/plainTextToTiptap.ts';
import { convertTiptapToPlainText } from './converters/tiptapToPlainText.ts';
import { isBlockVariable } from './templateVariableUtils.ts';
import VariableInsertPanel from './components/VariableInsertPanel.tsx';
import SuggestionPopup from './components/SuggestionPopup.tsx';
import { useSuggestionStore } from './suggestionStore.ts';
import { RichTextEditorSkeleton } from './RichTextEditorSkeleton.tsx';

interface PlainTextTemplateEditorProps {
  content: string;
  variables?: TemplateVariableDefinitionDto[];
  onChange?: (text: string) => void;
  disabled?: boolean;
  className?: string;
  error?: string;
  singleLine?: boolean;
}

const PlainTextTemplateEditor = (props: PlainTextTemplateEditorProps) => {
  if (props.variables === undefined) {
    return <RichTextEditorSkeleton className={props.className} />;
  }
  return <PlainTextTemplateEditorInner {...props} />;
};

const PlainTextTemplateEditorInner = ({
  content,
  variables,
  onChange,
  disabled = false,
  className,
  error,
  singleLine = false,
}: PlainTextTemplateEditorProps) => {
  const inlineVariables = useMemo(
    () => (variables ?? []).filter((v) => !isBlockVariable(v)),
    [variables],
  );

  const variablesRef = useRef(inlineVariables);
  variablesRef.current = inlineVariables;

  const tiptapContent = useMemo(
    () => convertPlainTextToTiptap(content, variables),
    [content, variables],
  );

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        code: false,
        heading: false,
        horizontalRule: false,
        bold: false,
        italic: false,
        strike: false,
        hardBreak: false,
      }),
      TemplateVariable,
      VariableSuggestion.configure({
        getVariables: () => variablesRef.current,
      }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: tiptapContent,
    editable: !disabled,
    editorProps: singleLine
      ? {
          handleKeyDown: (_view, event) => {
            if (event.key === 'Enter') {
              if (useSuggestionStore.getState().open) return false;
              return true;
            }
            return false;
          },
          transformPastedText: (text) => text.replace(/\r?\n/g, ' '),
        }
      : {},
  });

  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => {
      if (!onChange) return;
      onChange(convertTiptapToPlainText(editor.getHTML()));
    };
    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, onChange]);

  if (!editor) return null;

  const minHeightClass = singleLine
    ? '[&_.ProseMirror]:min-h-[24px]'
    : '[&_.ProseMirror]:min-h-[140px]';

  return (
    <VariablesProvider variables={inlineVariables}>
      <div
        className={cn(
          'overflow-hidden rounded-md border bg-basic-white',
          error ? 'border-error-100' : 'border-neutral-200',
          className,
        )}
      >
        {inlineVariables.length > 0 && (
          <VariableInsertPanel editor={editor} variables={inlineVariables} />
        )}
        <EditorContent
          editor={editor}
          className={cn(
            'text-sm [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:my-0.5',
            singleLine ? 'px-3 py-2' : 'px-4 py-3',
            minHeightClass,
          )}
        />
      </div>
      <SuggestionPopup />
    </VariablesProvider>
  );
};

export default PlainTextTemplateEditor;
